import React from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  SwapOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { theme } from '@/theme';

// Shared status presentation for the transfer list and detail page.
// Completed, pending, in flight, and retry scheduled keep their existing colors.
// Failed, retries exhausted, and rejected share the TNC error token.
const BRAND = {
  green: '#49A842',
  yellow: '#FFC700',
  cyan: '#01C1E3',
  grey: '#8c8c8c',
};

const failedColor = theme.token?.colorError ?? '';

export interface TransferStatusMeta {
  color: string;
  icon: React.ReactNode;
  progress: number;
  label: string;
  failed: boolean;
}

const STATUS_META: Record<string, TransferStatusMeta> = {
  completed: { color: BRAND.green, icon: <CheckCircleOutlined />, progress: 100, label: 'Completed', failed: false },
  failed: { color: failedColor, icon: <CloseCircleOutlined />, progress: 100, label: 'Failed', failed: true },
  retries_exhausted: { color: failedColor, icon: <CloseCircleOutlined />, progress: 100, label: 'Failed — retries exhausted', failed: true },
  in_flight: { color: BRAND.cyan, icon: <SyncOutlined spin />, progress: 60, label: 'In flight', failed: false },
  retry_scheduled: { color: BRAND.cyan, icon: <ClockCircleOutlined />, progress: 40, label: 'Retry scheduled', failed: false },
  pending: { color: BRAND.yellow, icon: <ClockCircleOutlined />, progress: 15, label: 'Pending', failed: false },
  rejected: { color: failedColor, icon: <StopOutlined />, progress: 100, label: 'Rejected', failed: false },
  resubmitted: { color: BRAND.grey, icon: <SwapOutlined />, progress: 100, label: 'Resubmitted', failed: false },
};

export interface TransferOutcomeMeta {
  label: string;
  color: string;
}

const neutralOutcome = 'var(--ant-color-text-secondary)';

const OUTCOME_META: Record<string, TransferOutcomeMeta> = {
  rejected: { label: 'Rejected', color: failedColor },
  start_failed: { label: "Couldn't start", color: failedColor },
  failed_final: { label: 'Failed — retries exhausted', color: failedColor },
  completed: { label: 'Completed', color: STATUS_META.completed.color },
  retry_requested: { label: 'Retry requested', color: neutralOutcome },
  resubmit_requested: { label: 'Resubmit requested', color: neutralOutcome },
  resubmitted: { label: 'Resubmitted to another path', color: neutralOutcome },
};

export function transferOutcomeDisplay(outcome?: string | null): TransferOutcomeMeta {
  return (
    OUTCOME_META[outcome || ''] || {
      label: outcome || 'Unknown',
      color: neutralOutcome,
    }
  );
}

export function transferStatusDisplay(status?: string | null): TransferStatusMeta {
  return (
    STATUS_META[status || ''] || {
      color: BRAND.grey,
      icon: <ClockCircleOutlined />,
      progress: 0,
      label: status || 'unknown',
      failed: false,
    }
  );
}

function triggeredByIdentity(triggeredBy?: string | null): string {
  const value = (triggeredBy || '').trim();
  if (value.startsWith('api:ad_hoc:')) {
    return value.slice('api:ad_hoc:'.length).trim().toLowerCase();
  }
  if (value.startsWith('manual:')) {
    return value.slice('manual:'.length).trim().toLowerCase();
  }
  return '';
}

export function triggeredByLabel(triggeredBy?: string | null): string {
  const value = triggeredBy || '';
  if (value.startsWith('api:ad_hoc:')) {
    const user = triggeredByIdentity(value);
    return user ? `Ad hoc by ${user}` : 'Ad hoc';
  }
  if (value.startsWith('manual:')) {
    const user = triggeredByIdentity(value);
    return user ? `Manual retry by ${user}` : 'Manual retry';
  }
  return 'scheduled';
}

const TRANSFER_ID_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Last path segment of a transfer id. UUIDs and empty ids have no file name. */
export function fileNameFromTransferId(id: string | undefined): string | undefined {
  try {
    if (typeof id !== 'string') {
      return undefined;
    }
    const trimmed = id.trim();
    if (!trimmed || TRANSFER_ID_UUID.test(trimmed)) {
      return undefined;
    }
    const segments = trimmed
      .split(/[\\/]+/)
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0);
    return segments[segments.length - 1] || undefined;
  } catch {
    return undefined;
  }
}

/** Requester email only. Display value; stored triggeredBy is unchanged. */
export function submittedByLabel(triggeredBy?: string | null): string {
  return triggeredByIdentity(triggeredBy) || '—';
}
